import { addDays, format, isWeekend, startOfDay } from 'date-fns';

import { ReadyToScheduleRow } from './hearing-schedule.models';

/**
 * Booking has no additional advance wait. The 180-day planning window is owned by enrollment.
 *
 * This is the client mirror of `CoreContextProceedingSchedulingOperations.MinimumScheduleLeadTimeDays`,
 * which is what actually enforces the rule on every placement. The two copies are kept in step by
 * `SchedulingLeadTimeMirrorTests`, which reads this literal and compares it to the server constant —
 * if they ever diverge, the calendar would offer a date the server rejects, which is exactly the
 * failure #1487 exists to remove.
 */
export const SCHEDULE_MIN_LEAD_TIME_DAYS = 0;

/**
 * The earliest day a hearing can be placed, or the reason no such day exists (#1487).
 *
 * `date` is null only when `blockedReason` is set: the hearing's Due Date falls before the
 * lead-time floor, so every candidate day violates one rule or the other.
 */
type SchedulingDateSource = Pick<
  ReadyToScheduleRow,
  'schedulingContext' | 'eligibilityNextEligibleDate'
>;

export interface FirstAvailableDate {
  readonly date: Date | null;
  readonly blockedReason: string | null;
}

/** Managed hearings use their workflow deadline, not a separate inmate determination. */
export function schedulingDueDate(
  candidate: SchedulingDateSource | null | undefined,
): string | null {
  return candidate?.schedulingContext
    ? candidate.schedulingContext.dueDate
    : (candidate?.eligibilityNextEligibleDate ?? null);
}

/**
 * The calendar day an ISO DATE names, read in UTC and rebuilt at local midnight so it can be
 * compared with the locally-computed floor.
 *
 * The Due Date / NLT (`eligibilityNextEligibleDate`) is a date, not a moment, and the server
 * compares it day-granular in UTC. Parsing it as an instant would shift it a day backwards for any
 * viewer behind UTC and wrongly report a conflict on a hearing that is due exactly on its first
 * available day.
 */
function parseCalendarDay(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return new Date(
    parsed.getUTCFullYear(),
    parsed.getUTCMonth(),
    parsed.getUTCDate(),
    0,
    0,
    0,
    0,
  );
}

/**
 * Saturday and Sunday are refused by the server's weekend rule, so a candidate day that lands on
 * one moves to the following Monday.
 */
function advancePastWeekend(day: Date): Date {
  let candidate = day;
  while (candidate.getDay() === 6 || candidate.getDay() === 0) {
    candidate = addDays(candidate, 1);
  }
  return candidate;
}

/** The lead-time floor: the earliest day the placement rules allow, before any per-hearing dates. */
function leadTimeFloor(now: Date): Date {
  // Booking validates its date against the server's UTC day. Rebuild that date at
  // local midnight for date-only controls; using the browser's current day offers
  // yesterday after UTC midnight to staff west of UTC.
  return addDays(new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()), SCHEDULE_MIN_LEAD_TIME_DAYS);
}

/**
 * The first day this hearing can actually be placed: today plus the minimum lead time, rolled
 * forward off a weekend. It mirrors the rules `CoreContextEzWorkflowRuntimeService` enforces when a
 * placement is attempted:
 *
 *  - not in the past and at least `SCHEDULE_MIN_LEAD_TIME_DAYS` out (the floor covers both);
 *  - never a Saturday or Sunday;
 *  - not after the hearing's Due Date / NLT, which comes from the workflow scheduling context (with the legacy inmate date as fallback).
 *
 * The candidate's `earliestScheduleStartUtc` (the per-inmate eligibility window) is deliberately
 * NOT consulted. The server treats it as advisory and never enforces it, and honouring it here used
 * to open the calendar on that window instead — the seeded windows sit around 30 days out, so the
 * view landed a fortnight past the first day a placement would actually succeed. The calendar must
 * snap to the first schedulable day, and that is the lead-time floor.
 *
 * `now` is injected so the result is deterministic under test.
 */
export function resolveFirstAvailableDate(
  candidate: SchedulingDateSource | null | undefined,
  now: Date = new Date(),
): FirstAvailableDate {
  const resolved = advancePastWeekend(leadTimeFloor(now));

  const dueDate = parseCalendarDay(schedulingDueDate(candidate));
  if (dueDate && resolved.getTime() > dueDate.getTime()) {
    // Both bounds are real rules, so there is no day that satisfies them together. Report it rather
    // than offering a date the server would refuse.
    return {
      date: null,
      blockedReason:
        `no booking date is available before the due date. The hearing is due by ${format(dueDate, 'MMM d, yyyy')}, but the earliest ` +
        `allowed booking date is ${format(resolved, 'MMM d, yyyy')}. Review the deadline and scheduling requirements before booking`,
    };
  }

  return { date: resolved, blockedReason: null };
}

/**
 * The day prefilled into the Add-to-Calendar picker and the Schedule Hearing dialog. It is the same
 * day the calendar opens on, so the scheduler confirms the date they were shown.
 *
 * This replaced a plain "one week out" fallback, which sat INSIDE the lead-time floor and therefore
 * prefilled a date the server would reject. When no day qualifies at all, the floor is offered so
 * the dialog still opens on a sane day and the server reports the due-date conflict.
 */
export function resolveScheduleStartDefault(
  candidate: SchedulingDateSource | null | undefined,
  now: Date = new Date(),
): Date {
  return (
    resolveFirstAvailableDate(candidate, now).date ??
    advancePastWeekend(leadTimeFloor(now))
  );
}

/** UI preflight only. The booking command revalidates the current deadline and business calendar. */
export function schedulingDateIssue(
  candidate: SchedulingDateSource,
  scheduled: Date,
  now: Date = new Date(),
): string | null {
  const available = resolveFirstAvailableDate(candidate, now);
  if (available.blockedReason) return available.blockedReason;
  if (startOfDay(scheduled).getTime() < leadTimeFloor(now).getTime())
    return 'The hearing cannot be scheduled in the past.';
  if (isWeekend(scheduled))
    return 'Hearings cannot be scheduled on a Saturday or Sunday.';
  const due = parseCalendarDay(schedulingDueDate(candidate));
  if (due && startOfDay(scheduled).getTime() > due.getTime())
    return `This hearing is due by ${format(due, 'MMM d, yyyy')}. Choose a date on or before its deadline.`;
  return null;
}

export function schedulingDueDay(candidate: SchedulingDateSource): Date | null {
  return parseCalendarDay(schedulingDueDate(candidate));
}
